import * as THREE from 'three';
import { TeleportationSystem } from './TeleportationSystem';
import { CameraController } from './CameraController';
import { RoomManager } from './RoomManager';
import { ArtworkManager } from './ArtworkManager';
import { InteractionHandler } from './InteractionHandler';
import { MultiRoomManager } from './MultiRoomManager';
import { BackgroundLoader } from './BackgroundLoader';

// Vanilla Three.js Gallery Engine (Refactored)
export class VanillaGalleryEngine {
  constructor(container, loadingManager = null) {
    this.container = container;
    this.loadingManager = loadingManager;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.clock = new THREE.Clock();

    // WebGL context state
    this.contextLost = false;

    // Module instances
    this.teleportationSystem = null;
    this.cameraController = null;
    this.roomManager = null;
    this.artworkManager = null;
    this.interactionHandler = null;
    this.multiRoomManager = null;
    this.backgroundLoader = null;
    
    // Multi-room loading feature flag
    this.multiRoomEnabled = true; // Enable container system with fixes

    try {
      this.init();
      this.animate();
    } catch (error) {
      console.error('Error in VanillaGalleryEngine constructor:', error);
    }
  }

  init() {
    // Scene setup
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xcccccc);
    this.scene.fog = new THREE.Fog(0xcccccc, 10, 60);

    // Camera setup
    const isMobile = 'ontouchstart' in window;
    const fov = isMobile ? 75 : 50; // Narrower FOV on desktop
    this.camera = new THREE.PerspectiveCamera(
      fov,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    // HARDCODED: Initial camera position at (10.5, 1.8, 0) looking straight ahead at eye level
    this.camera.position.set(11, 1.8, 0.0);
    this.camera.lookAt(0, 1.8, 0); // Look at eye level (y: 1.8), not at floor

    // Renderer setup
    this.renderer = new THREE.WebGLRenderer({
      antialias: !('ontouchstart' in window), // Disable AA on mobile
      alpha: false,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false, // Better performance
      stencil: false, // Disable stencil buffer for performance
      depth: true
    });

    // Set pixel ratio with performance considerations
    const pixelRatio = Math.min(window.devicePixelRatio, 2); // Cap at 2x for performance
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);

    // Mobile performance optimizations
    this.renderer.shadowMap.enabled = false; // Always disable shadows for performance
    this.renderer.physicallyCorrectLights = false; // Better performance
    
    // Enable automatic memory management
    this.renderer.info.autoReset = true;

    // Add renderer to container
    this.container.appendChild(this.renderer.domElement);

    // WebGL context event listeners
    this.renderer.domElement.addEventListener('webglcontextlost', this.onContextLost.bind(this), false);
    this.renderer.domElement.addEventListener('webglcontextrestored', this.onContextRestored.bind(this), false);

    // Initialize modules
    this.initializeModules();

    // Event listeners
    this.setupEventListeners();

    // Window resize
    window.addEventListener('resize', this.onWindowResize.bind(this));
  }

  /**
   * Initialize all modules
   */
  initializeModules() {
    // Initialize multi-room manager FIRST (required by other modules)
    if (this.multiRoomEnabled) {
      this.multiRoomManager = new MultiRoomManager(this.scene);
    }

    // Initialize modules with loading manager
    this.teleportationSystem = new TeleportationSystem(this.scene, this.camera);
    this.cameraController = new CameraController(this.camera, this.clock, this.scene);
    this.roomManager = new RoomManager(this.scene, this.loadingManager, this.multiRoomManager);
    this.artworkManager = new ArtworkManager(this.scene, this.loadingManager);
    this.interactionHandler = new InteractionHandler(
      this.scene, 
      this.camera, 
      this.teleportationSystem, 
      this.cameraController
    );
    
    // Initialize background loader
    if (this.multiRoomEnabled) {
      this.backgroundLoader = new BackgroundLoader(
        this.multiRoomManager,
        this.roomManager,
        this.artworkManager
      );
      console.log('🚀 Multi-room system enabled');
    } else {
      console.log('📋 Using traditional single-room loading');
    }
  }

  setupEventListeners() {
    const canvas = this.renderer.domElement;

    canvas.addEventListener('pointerdown', this.onPointerDown.bind(this));
    canvas.addEventListener('pointermove', this.onPointerMove.bind(this));
    canvas.addEventListener('pointerup', this.onPointerUp.bind(this));
    canvas.addEventListener('pointerleave', () => {
      this.cameraController.resetPointerState();
    });
    canvas.addEventListener('pointercancel', () => {
      this.cameraController.resetPointerState();
    });
    canvas.addEventListener('wheel', this.onWheel.bind(this), { passive: false });

    // Keyboard controls for WASD movement
    window.addEventListener('keydown', this.onKeyDown.bind(this));
    window.addEventListener('keyup', this.onKeyUp.bind(this));

    // Touch event prevention - ONLY prevent default on canvas when not interacting with UI
    // This allows scrolling on overlays/modals while preventing canvas zoom/scroll
    canvas.addEventListener('touchstart', (e) => {
      // Only prevent if touching the canvas directly (not UI overlays)
      if (e.target === canvas) {
        e.preventDefault();
      }
    }, { passive: false });

    canvas.addEventListener('touchmove', (e) => {
      // Only prevent if touching the canvas directly (not UI overlays)
      if (e.target === canvas) {
        e.preventDefault();
      }
    }, { passive: false });

    canvas.addEventListener('touchend', (e) => {
      // Only prevent if touching the canvas directly (not UI overlays)
      if (e.target === canvas) {
        e.preventDefault();
      }
    }, { passive: false });

    // Canvas styling
    canvas.style.width = '100vw';
    canvas.style.height = '100vh';
    canvas.style.cursor = 'grab';
    canvas.style.touchAction = 'none';
    canvas.style.userSelect = 'none';
    canvas.style.webkitUserSelect = 'none';
    canvas.style.webkitTouchCallout = 'none';
  }

  // Event handlers that delegate to modules
  onKeyDown(event) {
    this.cameraController.onKeyDown(event);
  }

  onKeyUp(event) {
    this.cameraController.onKeyUp(event);
  }

  onPointerDown(event) {
    this.cameraController.onPointerDown(event);
  }

  onPointerMove(event) {
    this.cameraController.onPointerMove(event);
  }

  onPointerUp(event) {
    const isTap = this.cameraController.onPointerUp(event);
    if (isTap) {
      // Get objects from the correct source (container vs scene)
      let floorMeshes, floor, interactiveObjects;
      
      if (this.multiRoomEnabled && this.multiRoomManager) {
        // Multi-room mode: get objects from current visible container
        floorMeshes = this.multiRoomManager.getCurrentRoomFloorMeshes();
        floor = floorMeshes.length > 0 ? floorMeshes[0] : null;
        interactiveObjects = this.multiRoomManager.getCurrentRoomInteractiveObjects();
        // Interaction check in visible container
      } else {
        // Traditional mode: get objects from managers directly
        floorMeshes = this.roomManager.getFloorMeshes();
        floor = this.roomManager.getFloor();
        interactiveObjects = this.artworkManager.getInteractiveObjects();
      }
      
      this.interactionHandler.handleInteraction(
        event, 
        floorMeshes, 
        floor, 
        interactiveObjects,
        interactiveObjects // Pass all visible room objects for collision detection
      );
    }
  }

  onWheel(event) {
    this.cameraController.onWheel(event);
  }

  onWindowResize() {
    this.cameraController.onWindowResize();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  // Room management methods
  async createRoom(showroom, targetContainer = null) {
    // Determine which container to use for room assets
    let roomContainer;
    
    if (this.multiRoomEnabled && this.multiRoomManager) {
      // Multi-room mode: use isolated container
      roomContainer = targetContainer || this.multiRoomManager.getRoomContainer(showroom.id);
      // Creating room in container
      
      // Mark room as loading in multi-room manager
      this.multiRoomManager.markRoomAsLoading(showroom.id);
    } else {
      // Traditional mode: use scene directly (backward compatibility)
      roomContainer = this.scene;
      console.log('🏗️ Creating room in scene (traditional mode)');
    }
    
    // Create room with appropriate container
    await this.roomManager.createRoom(showroom, roomContainer);
    
    // Validate that the room was actually created
    const hasFloor = this.roomManager.getFloor() || this.roomManager.getFloorMeshes().length > 0;
    if (!hasFloor) {
      console.warn('⚠️ Room creation completed but no floor found - may indicate loading failure');
      
      // Mark as error in multi-room mode
      if (this.multiRoomEnabled && this.multiRoomManager) {
        this.multiRoomManager.markRoomAsError(showroom.id, 'No floor found after creation');
      }
    } else {
      // Mark as loaded in multi-room mode
      if (this.multiRoomEnabled && this.multiRoomManager) {
        this.multiRoomManager.markRoomAsLoaded(showroom.id);
        
        // Cache SAFE spawn position AND direction for instant switching performance  
        const floorCenter = this.roomManager.getFloorCenter();
        if (floorCenter && targetContainer) {
          // Find safe position and cache that instead of raw floor center
          const safePosition = this.findSafeSpawnPosition(floorCenter, [], []); // No items during room creation
          const { lookDirection } = this.calculateOptimalCameraDirection(safePosition, targetContainer);
          
          targetContainer.userData.safeSpawnPosition = {
            x: safePosition.x,
            y: safePosition.y, 
            z: safePosition.z
          };
          targetContainer.userData.cameraDirection = {
            x: lookDirection.x,
            z: lookDirection.z
          };
        }
        
        // CRITICAL: Make this room visible if it's being created as the current room
        if (!targetContainer) { // If no target container specified, this is the current room
          // Making room visible
          this.multiRoomManager.switchToRoom(showroom.id);

          // Update camera controller with current room for collision detection
          const currentContainer = this.multiRoomManager.getCurrentRoomContainer();
          if (currentContainer && this.cameraController) {
            this.cameraController.setCurrentRoomContainer(currentContainer);
          }
        }
      }
    }
    
    return hasFloor;
  }


  // Camera positioning methods
  positionCameraAtRoomCenter(placements = [], items = [], roomId = null) {
//    console.log('🎯 Positioning camera at safe room center');

    // HARDCODED: Override room center logic - use fixed position (10.5, 1.8, 0) looking straight ahead at eye level
    const hardcodedPosition = { x: 10.5, y: 1.8, z: 0.0 };

    // Position camera at hardcoded location
    this.camera.position.set(hardcodedPosition.x, hardcodedPosition.y, hardcodedPosition.z);

    // Reset camera controller state FIRST
    this.cameraController.resetToPosition(hardcodedPosition.x, hardcodedPosition.y, hardcodedPosition.z);

    // THEN set camera direction to look straight ahead at eye level (not at floor)
    this.camera.lookAt(0, 1.8, 0);

    /* ORIGINAL CODE - commented out for hardcoded position
    // Get floor center position
    const floorCenter = this.roomManager.getFloorCenter(roomId);
    if (!floorCenter) {
      console.warn('No floor center found, keeping current camera position');
      return;
    }

    // Check if center position is safe (no items nearby)
    const safePosition = this.findSafeSpawnPosition(floorCenter, placements, items);

    // Calculate optimal camera direction (face away from closest wall)
    const { lookDirection } = this.calculateOptimalCameraDirection(safePosition);

    // Position camera at safe location
    this.camera.position.set(safePosition.x, 1.8, safePosition.z);

    // Reset camera controller state FIRST
    this.cameraController.resetToPosition(safePosition.x, 1.8, safePosition.z);

    // THEN set camera direction (after controller reset)
    this.camera.lookAt(
      safePosition.x + lookDirection.x * 5,
      1.8,
      safePosition.z + lookDirection.z * 5
    );
    */

  }

  findSafeSpawnPosition(centerPosition, placements = [], items = []) {
    const minDistance = 2; // Minimum distance from any item
    
    // Check if center is safe
    if (this.isPositionSafe(centerPosition, placements, items, minDistance)) {
      return centerPosition;
    }
    
    // Try positions in expanding circles around center
    const angles = [0, Math.PI/4, Math.PI/2, 3*Math.PI/4, Math.PI, 5*Math.PI/4, 3*Math.PI/2, 7*Math.PI/4];
    const distances = [1, 2, 3, 4]; // Try distances from center
    
    for (const distance of distances) {
      for (const angle of angles) {
        const testPosition = {
          x: centerPosition.x + Math.cos(angle) * distance,
          y: centerPosition.y,
          z: centerPosition.z + Math.sin(angle) * distance
        };
        
        if (this.isPositionSafe(testPosition, placements, items, minDistance)) {
          return testPosition;
        }
      }
    }
    
    // Fallback to center if no safe position found
    return centerPosition;
  }

  isPositionSafe(position, placements, items, minDistance) {
    // Check distance to all placed items
    for (const placement of placements) {
      const distance = Math.sqrt(
        Math.pow(position.x - placement.positionX, 2) + 
        Math.pow(position.z - placement.positionZ, 2)
      );
      
      if (distance < minDistance) {
        return false;
      }
    }
    
    // CRITICAL: Check if position is inside or too close to walls
    if (this.isPositionInsideWall(position)) {
      return false;
    }
    
    return true;
  }
  
  isPositionInsideWall(position) {
    if (!this.teleportationSystem) {
      return false; // Can't check, assume safe
    }
    
    try {
      // Cast rays in multiple directions to detect walls
      const testHeight = 1.8; // Camera height
      const testPosition = new THREE.Vector3(position.x, testHeight, position.z);
      const directions = [
        new THREE.Vector3(1, 0, 0),   // Right
        new THREE.Vector3(-1, 0, 0),  // Left  
        new THREE.Vector3(0, 0, 1),   // Forward
        new THREE.Vector3(0, 0, -1)   // Backward
      ];
      
      const raycaster = new THREE.Raycaster();
      const minWallDistance = 0.8; // Must be at least 0.8 units from any wall
      
      // Get all meshes for collision detection
      const allMeshes = this.getWallMeshesForDirection();
      
      if (allMeshes.length === 0) {
        return false;
      }
      
      for (const direction of directions) {
        raycaster.set(testPosition, direction);
        const intersects = raycaster.intersectObjects(allMeshes, true);
        
        if (intersects.length > 0 && intersects[0].distance < minWallDistance) {
          return true; // Too close to wall or inside wall
        }
      }
      
      return false; // Position is safe from walls
    } catch (error) {
      return false; // Assume safe if check fails
    }
  }
  
  calculateOptimalCameraDirection(position, roomContainer = null) {
    if (!this.teleportationSystem) {
      // Fallback direction if no teleportation system
      return { lookDirection: { x: 0, z: -1 } }; // Look forward
    }
    
    try {
      const testPosition = new THREE.Vector3(position.x, 1.8, position.z);
      const directions = [
        { name: 'forward', vector: new THREE.Vector3(0, 0, -1) },
        { name: 'right', vector: new THREE.Vector3(1, 0, 0) },
        { name: 'backward', vector: new THREE.Vector3(0, 0, 1) },
        { name: 'left', vector: new THREE.Vector3(-1, 0, 0) }
      ];
      
      const raycaster = new THREE.Raycaster();
      let maxDistance = 0;
      let bestDirection = { x: 0, z: -1 }; // Default forward
      
      // Get wall meshes to raycast against
      const wallMeshes = this.getWallMeshesForDirection(roomContainer);
      
      // Find closest mesh in any direction
      let minDistance = Infinity;
      let closestDirection = null;
      
      for (const dir of directions) {
        raycaster.set(testPosition, dir.vector);
        const intersects = raycaster.intersectObjects(wallMeshes, true);
        
        if (intersects.length > 0) {
          const distance = intersects[0].distance;
          if (distance < minDistance) {
            minDistance = distance;
            closestDirection = dir.vector;
          }
        }
      }
      
      // Face opposite direction from closest mesh
      if (closestDirection) {
        bestDirection = { x: -closestDirection.x, z: -closestDirection.z };
      }
      
      return { lookDirection: bestDirection, distance: minDistance };
      
    } catch (error) {
      console.warn('Camera direction calculation failed:', error);
      return { lookDirection: { x: 0, z: -1 } }; // Fallback
    }
  }
  
  getWallMeshesForDirection(roomContainer = null) {
    if (this.multiRoomEnabled && this.multiRoomManager) {
      // Use specific room container if provided, otherwise current room
      const container = roomContainer || this.multiRoomManager.getCurrentRoomContainer();
      
      if (container) {
        const allMeshes = [];
        container.traverse((child) => {
          if (child.isMesh) {
            allMeshes.push(child);
          }
        });
        return allMeshes; // Return ALL meshes for collision detection
      }
    }
    
    // Fallback to teleportation system
    return this.teleportationSystem.getWallMeshes();
  }

  clearRoom() {
    console.log('🧹 VanillaGalleryEngine: Starting room cleanup');
    
    // Clear room and artworks
    this.roomManager.clearRoom();
    this.artworkManager.clearArtworks();
    
    // Force renderer cleanup
    if (this.renderer) {
      this.renderer.renderLists.dispose();
      
      // Clear WebGL state
      this.renderer.state.reset();
      
      console.log('🧹 Renderer cleanup complete');
    }
  }

  // Artwork management methods
  async addArtwork(item, placement, targetContainer = null) {
    // Determine which container to use for artwork
    let artworkContainer;
    
    if (this.multiRoomEnabled && this.multiRoomManager) {
      // Multi-room mode: use room-specific container
      const roomId = placement.showroomId;
      artworkContainer = targetContainer || this.multiRoomManager.getRoomContainer(roomId);
      console.log(`🖼️ Adding artwork ${item.name} to room ${roomId} container`);
    } else {
      // Traditional mode: artwork manager handles scene directly (backward compatibility)
      artworkContainer = null; // Let ArtworkManager use scene directly
      console.log(`🖼️ Adding artwork ${item.name} to scene (traditional mode)`);
    }
    
    // Add artwork with appropriate container
    await this.artworkManager.addArtwork(
      item, 
      placement, 
      this.roomManager.getPlacementSystem(),
      artworkContainer
    );
    console.log(`✅ Artwork fully loaded and added: ${item.name}`);
  }

  placeItemOnSurface(item, surfaceId, gridX, gridY, options = {}) {
    return this.artworkManager.placeItemOnSurface(
      item, 
      surfaceId, 
      gridX, 
      gridY, 
      options, 
      this.roomManager.getPlacementSystem()
    );
  }

  // Teleportation methods
  async teleportToObjectInRoom(item, placement, showroomData, allPlacements, allItems) {
    await this.interactionHandler.teleportToObjectInRoom(
      item, 
      placement, 
      showroomData, 
      allPlacements, 
      allItems, 
      this.roomManager, 
      this.artworkManager
    );
  }

  handleArtworkTeleportation(item, placement, fromCatalog = false, showPopup = true) {
    this.interactionHandler.handleArtworkTeleportation(item, placement, fromCatalog, showPopup);
  }

  // Callback setters
  setOnInteraction(callback) {
    this.interactionHandler.setOnInteraction(callback);
  }

  setOnRoomChangeRequest(callback) {
    this.interactionHandler.setOnRoomChangeRequest(callback);
  }

  // Multi-room loading methods
  /**
   * Start background loading of all rooms
   * @param {Array} showrooms - All showroom data
   * @param {Array} items - All item data  
   * @param {Array} placements - All placement data
   * @param {string|number} currentRoomId - Currently active room ID
   */
  async startBackgroundLoading(showrooms, items, placements, currentRoomId) {
    if (!this.multiRoomEnabled || !this.backgroundLoader) {
      console.log('📋 Multi-room loading disabled, skipping background loading');
      return;
    }
    
    console.log(`🚀 Starting background loading for ${showrooms.length} rooms (excluding current: ${currentRoomId})`);
    
    // Queue rooms for background loading
    this.backgroundLoader.queueRoomsForLoading(showrooms, items, placements, currentRoomId);
    
    // Start the intelligent background loading process
    await this.backgroundLoader.startBackgroundLoading();
  }

  /**
   * Switch to a different room (instant if loaded, loading screen if not)
   * @param {string|number} roomId - Target room ID
   * @param {Array} placements - All placements data
   * @param {Array} items - All items data
   * @returns {boolean} True if switch was successful/initiated
   */
  switchToRoom(roomId, placements = [], items = []) {
    if (!this.multiRoomEnabled || !this.multiRoomManager) {
      console.log('📋 Multi-room disabled, using traditional room switching');
      return false; // Let existing system handle room switching
    }
    
    console.log(`🔄 Switching to room ${roomId}`);
    
    // Check if room is ready for instant switching
    const isReady = this.multiRoomManager.isRoomReady(roomId);
    
    if (isReady) {
      // Instant switch - position camera BEFORE revealing room
      const roomPlacements = placements.filter(p => p.showroomId === roomId);
      
      const success = this.multiRoomManager.switchToRoom(roomId, () => {
        // This callback executes while screen is black - position camera now
        this.positionCameraAtRoomCenter(roomPlacements, items, roomId);
      });

      if (success) {
        console.log(`✅ Instantly switched to room ${roomId}`);

        // Update camera controller with current room for collision detection
        const currentContainer = this.multiRoomManager.getCurrentRoomContainer();
        if (currentContainer && this.cameraController) {
          this.cameraController.setCurrentRoomContainer(currentContainer);
        }

        return true;
      }
    } else {
      console.log(`⏳ Room ${roomId} not ready - will need to show loading`);
      return false; // Caller should show loading screen and load room normally
    }
    
    return false;
  }


  /**
   * Get loading progress for all rooms
   * @returns {Object} Progress information
   */
  getLoadingProgress() {
    if (!this.multiRoomEnabled || !this.multiRoomManager || !this.backgroundLoader) {
      return { progress: 1, loaded: 1, total: 1 }; // Traditional mode - always "loaded"
    }
    
    const roomProgress = this.multiRoomManager.getLoadingProgress();
    const loaderProgress = this.backgroundLoader.getProgress();
    
    return {
      ...roomProgress,
      currentlyLoading: loaderProgress.currentlyLoading,
      isActive: loaderProgress.isActive,
      isPaused: loaderProgress.isPaused,
      currentFPS: loaderProgress.currentFPS,
      queueRemaining: loaderProgress.queueRemaining
    };
  }

  /**
   * Check if a specific room is ready for instant switching
   * @param {string|number} roomId - Room ID to check
   * @returns {boolean} True if room is loaded and ready
   */
  isRoomReady(roomId) {
    if (!this.multiRoomEnabled || !this.multiRoomManager) {
      return false; // In traditional mode, rooms are never "pre-loaded"
    }
    
    return this.multiRoomManager.isRoomReady(roomId);
  }

  /**
   * Get current room ID
   * @returns {string|number|null} Current room ID
   */
  getCurrentRoomId() {
    if (!this.multiRoomEnabled || !this.multiRoomManager) {
      return null; // Traditional mode doesn't track current room ID
    }
    
    return this.multiRoomManager.getCurrentRoomId();
  }

  /**
   * Enable/disable multi-room functionality
   * @param {boolean} enabled - Whether to use multi-room system
   */
  setMultiRoomEnabled(enabled) {
    console.log(`🔧 ${enabled ? 'Enabling' : 'Disabling'} multi-room functionality`);
    
    this.multiRoomEnabled = enabled;
    
    if (this.multiRoomManager) {
      this.multiRoomManager.setMultiRoomEnabled(enabled);
    }
    
    if (this.backgroundLoader && !enabled) {
      this.backgroundLoader.stopLoading();
    }
  }

  // Main animation loop
  animate() {
    requestAnimationFrame(this.animate.bind(this));

    // Don't render if WebGL context is lost
    if (this.contextLost) {
      return;
    }

    const delta = this.clock.getDelta();

    // Update camera animation
    this.cameraController.updateCameraAnimation(delta);

    // Update WASD movement
    this.cameraController.updateMovement();

    // Rotate sculptures
    this.artworkManager.updateRotations();

    if (this.renderer && this.scene && this.camera && !this.contextLost) {
      try {
        this.renderer.render(this.scene, this.camera);
      } catch (error) {
        console.warn('Render error, possibly due to context loss:', error);
        this.contextLost = true;
      }
    }
  }

  // WebGL context management
  onContextLost(event) {
    event.preventDefault();
    console.warn('WebGL context lost - pausing rendering');
    this.contextLost = true;
  }

  onContextRestored() {
    this.contextLost = false;
    // Context will be automatically restored by Three.js
  }

  // Cleanup
  dispose() {
    // Stop animation loop first
    this.contextLost = true;

    // Remove event listeners
    window.removeEventListener('resize', this.onWindowResize.bind(this));

    // Clear all scene objects with proper disposal
    this.clearRoom();

    // Dispose renderer and remove from DOM
    if (this.renderer) {
      // Remove canvas from container
      if (this.container && this.renderer.domElement.parentNode) {
        this.container.removeChild(this.renderer.domElement);
      }

      // Dispose WebGL context
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      this.renderer = null;
    }

    // Clear references
    this.scene = null;
    this.camera = null;
    this.teleportationSystem = null;
    this.cameraController = null;
    this.roomManager = null;
    this.artworkManager = null;
    this.interactionHandler = null;
  }
}